import type { Goal, Outcome } from '../../../api/strategy-client/strategy-client.type';

/**
 * Gets the relative time string for an end date
 *
 * @param {string | null | undefined} endDate - ISO date string
 * @returns {string} Relative time string (e.g., "in 2 weeks", "in 5 days")
 */
const getRelativeEndDate = (endDate: string | null | undefined): string => {
  if (!endDate) return 'N/A';

  const end = new Date(endDate);
  const today = new Date();
  const diffTime = end.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return 'Overdue';
  } else if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Tomorrow';
  } else if (diffDays < 7) {
    return `in ${diffDays} days`;
  } else if (diffDays < 14) {
    return 'in 1 week';
  } else if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `in ${weeks} weeks`;
  } else if (diffDays < 60) {
    return 'in 1 month';
  } else {
    const months = Math.floor(diffDays / 30);
    return `in ${months} months`;
  }
};

/**
 * Checks if an outcome is within committed weeks
 *
 * @param {Outcome | Goal} strategyEntity - Strategy entity to check
 * @param {number} weeks - Number of weeks to check
 * @returns {boolean} True if outcome is within committed weeks, false otherwise
 */
const isStrategyEntityWithinCommittedWeeks = (strategyEntity: Outcome | Goal, weeks: number): boolean => {
  if (!strategyEntity.end_date) {
    return false;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const endDate = new Date(strategyEntity.end_date);
  endDate.setHours(0, 0, 0, 0);

  const futureDate = new Date(today);
  futureDate.setDate(futureDate.getDate() + weeks * 7);

  return endDate >= today && endDate <= futureDate;
};

export { getRelativeEndDate, isStrategyEntityWithinCommittedWeeks };
